/**
 * @fileoverview iris-case-answers — IRIS's case-bounded answer layer (CAP 6).
 *
 * Foundry analog: AIP Assist / AIP Logic answering ONLY from objects bound to
 * the active Object Set (the case knowledge graph) — no external retrieval, no
 * predictions. Foundry's assistant cites the objects it drew from; this module
 * mirrors that by returning a structured answer plus `SourceRef[]` citations
 * resolved entirely from the case data the caller hands in.
 *
 * This is a DETERMINISTIC, MOCK, client-side responder. It intent-matches the
 * spec example queries (people in reports, vehicles, officers/reports, active
 * CalECPA deadlines, "draft a warrant for [phone]") against the case's mock
 * knowledge graph and answers from it alone. When no rule matches it returns
 * `null` so the caller can fall back to the existing Bedrock chat path.
 *
 * No network. No external services. No fabricated facts beyond what is present
 * in the supplied `CaseKnowledge`.
 */

import {
  WARRANT_TYPE_LABELS,
  type Person,
  type EvidenceItem,
  type Warrant,
  type SurveillanceClip,
  type CaseTimelineEvent,
  type Gap,
  type SourceRef,
  type WarrantType,
} from "./api";
import type { IntelligenceEntity } from "./entities-ontology";

/**
 * The slice of the active case the answerer reasons over. The host joins these
 * from the existing query hooks (`usePersons`, `useEvidence`, …) and the gap
 * layer (`useCaseGaps`) before opening IRIS, so every answer stays bounded to
 * one case.
 */
export interface CaseKnowledge {
  caseNumber?: string;
  caseTitle?: string;
  persons: Person[];
  evidence: EvidenceItem[];
  warrants: Warrant[];
  surveillance: SurveillanceClip[];
  timeline: CaseTimelineEvent[];
  entities: IntelligenceEntity[];
  gaps: Gap[];
}

/** A grounded answer: rendered text + the source documents it cites. */
export interface CaseAnswer {
  reply: string;
  sourceRefs: SourceRef[];
  /** Optional structured warrant-draft hand-off (the "draft a warrant" intent). */
  draftHandoff?: { type: WarrantType; phone: string };
}

// -- Formatting helpers (presentation only; never invent data) ---------------

const ROLE_LABELS: Record<Person["role"], string> = {
  suspect: "Suspect",
  victim: "Victim",
  witness: "Witness",
  officer: "Officer",
  personOfInterest: "Person of interest",
  associate: "Associate",
};

/** Below this, IronCase flags an extracted value as uncertain (spec CAP 6). */
const LOW_CONFIDENCE = 0.8;

function roleLabel(role: Person["role"]): string {
  return ROLE_LABELS[role] ?? role;
}

/** "the initial report" = the case's primary DR report (no -S suffix). */
function isInitialReport(reportNumber: string): boolean {
  return /^DR\s+\d{2}-\d{4,5}$/i.test(reportNumber.trim());
}

/** Reduce a person's `sourceRefs` to its first citation, if any. */
function firstRef(p: { sourceRefs?: SourceRef[] }): SourceRef | undefined {
  return p.sourceRefs && p.sourceRefs.length ? p.sourceRefs[0] : undefined;
}

/** Strip a phone string to bare digits for tolerant matching across formats. */
function digits(s: string): string {
  return s.replace(/\D+/g, "");
}

/** Append a low-confidence advisory when an extracted value is below threshold. */
function confidenceNote(confidence: number | undefined): string {
  return confidence != null && confidence < LOW_CONFIDENCE
    ? " (low confidence — verify)"
    : "";
}

function dedupeRefs(refs: SourceRef[]): SourceRef[] {
  const seen = new Set<string>();
  const out: SourceRef[] = [];
  for (const r of refs) {
    const key = `${r.evidenceId}|${r.label}|${r.locator ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

// -- Intent matchers ---------------------------------------------------------
// Each returns a CaseAnswer or null. `q` is the lowercased, trimmed query.

/**
 * "Who are all the people named in the initial reports?"
 * Persons whose `reportsMentionedIn` includes the primary DR report.
 */
function answerPeopleInReports(q: string, k: CaseKnowledge): CaseAnswer | null {
  const wantsPeople =
    /\b(people|persons|person|who)\b/.test(q) &&
    /\b(name|mention|report|listed)/.test(q);
  if (!wantsPeople) return null;

  const initialOnly = /\binitial\b/.test(q);
  const matched = k.persons.filter((p) =>
    (p.reportsMentionedIn ?? []).some((r) =>
      initialOnly ? isInitialReport(r) : true,
    ),
  );
  if (!matched.length) return null;

  const sourceRefs: SourceRef[] = [];
  const lines = matched.map((p) => {
    const ref = firstRef(p);
    if (ref) sourceRefs.push(ref);
    const reports = (p.reportsMentionedIn ?? []).join(", ") || "—";
    return `• ${p.fullName} — ${roleLabel(p.role)}${confidenceNote(p.confidence)}\n    Reports: ${reports}${ref ? `\n    Source: ${ref.label}${ref.locator ? ` (${ref.locator})` : ""}` : ""}`;
  });

  const scope = initialOnly ? "named in the initial report(s)" : "named across the case reports";
  const reply =
    `${matched.length} ${matched.length === 1 ? "person is" : "people are"} ${scope} on ${k.caseNumber ?? "this case"}:\n\n` +
    lines.join("\n\n") +
    `\n\nAll names are drawn from this case's reports only — no external lookups.`;

  return { reply, sourceRefs: dedupeRefs(sourceRefs) };
}

/**
 * "What vehicles have been identified?"
 * Vehicle entities from the case knowledge graph + their citations.
 */
function answerVehicles(q: string, k: CaseKnowledge): CaseAnswer | null {
  if (!/\b(vehicle|vehicles|car|cars|plate|plates|truck|suv|sedan)\b/.test(q)) return null;

  const vehicles = k.entities.filter((e) => e.kind === "vehicle");
  if (!vehicles.length) {
    return {
      reply: "No vehicles have been identified on this case yet.",
      sourceRefs: [],
    };
  }

  const sourceRefs: SourceRef[] = [];
  const lines = vehicles.map((v) => {
    const cite = v.attributes[0]?.sourceCitation;
    if (cite) sourceRefs.push({ evidenceId: v.id, label: cite });
    const attrs = v.attributes
      .map((a) => `${a.name}: ${a.value}`)
      .join("; ");
    return `• ${v.displayName}\n    ${attrs}`;
  });

  const reply =
    `${vehicles.length} ${vehicles.length === 1 ? "vehicle has" : "vehicles have"} been identified on ${k.caseNumber ?? "this case"}:\n\n` +
    lines.join("\n\n") +
    `\n\nIdentified from this case's evidence only.`;

  return { reply, sourceRefs: dedupeRefs(sourceRefs) };
}

/**
 * "Which officers submitted reports?"
 * Distinct officers from timeline authors + person records with the officer
 * role, with the reports each is tied to.
 */
function answerOfficers(q: string, k: CaseKnowledge): CaseAnswer | null {
  const wantsOfficers =
    /\b(officer|detective|deputy|cop)/.test(q) &&
    /\b(report|submit|author|wrote|filed|prepared)/.test(q);
  if (!wantsOfficers) return null;

  // Build officer -> set of report numbers from the timeline (authoritative for
  // "who submitted which report"), then enrich from officer-role persons.
  const byOfficer = new Map<string, Set<string>>();
  for (const ev of k.timeline) {
    const officer = ev.officer?.trim();
    if (!officer) continue;
    const set = byOfficer.get(officer) ?? new Set<string>();
    if (ev.reportNumber) set.add(ev.reportNumber);
    byOfficer.set(officer, set);
  }
  for (const p of k.persons) {
    if (p.role !== "officer") continue;
    const label = p.badge ? `${p.fullName} ${p.badge}` : p.fullName;
    const set = byOfficer.get(label) ?? new Set<string>();
    for (const r of p.reportsMentionedIn ?? []) set.add(r);
    byOfficer.set(label, set);
  }

  if (!byOfficer.size) return null;

  const lines = Array.from(byOfficer.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([officer, reports]) => {
      const list = Array.from(reports).sort();
      return `• ${officer} — ${list.length ? list.join(", ") : "report number not recorded"}`;
    });

  const reply =
    `${byOfficer.size} ${byOfficer.size === 1 ? "officer" : "officers"} authored or submitted reports on ${k.caseNumber ?? "this case"}:\n\n` +
    lines.join("\n") +
    `\n\nDrawn from the case's report log and personnel records.`;

  return { reply, sourceRefs: [] };
}

/**
 * "What CalECPA deadlines are active?"
 * Category-C gaps (CalECPA-driven and other time-sensitive) with countdowns.
 */
function answerCalecpaDeadlines(q: string, k: CaseKnowledge): CaseAnswer | null {
  const wantsDeadlines =
    /\b(calecpa|deadline|deadlines|countdown|time.?sensitive)\b/.test(q) ||
    /\b(expir|notification window|retention window|notice due)/.test(q);
  if (!wantsDeadlines) return null;

  const now = Date.now();
  const categoryC = k.gaps.filter((g) => g.category === "C");
  if (!categoryC.length) {
    return {
      reply: "No active time-sensitive (CalECPA / retention) deadlines on this case right now.",
      sourceRefs: [],
    };
  }

  // CalECPA-specific (notification) flags first, then other Category-C.
  const ordered = [...categoryC].sort((a, b) => {
    const aw = a.ruleId.startsWith("C-a") ? 0 : 1;
    const bw = b.ruleId.startsWith("C-a") ? 0 : 1;
    if (aw !== bw) return aw - bw;
    const at = a.countdownTo ? Date.parse(a.countdownTo) : Infinity;
    const bt = b.countdownTo ? Date.parse(b.countdownTo) : Infinity;
    return at - bt;
  });

  const sourceRefs: SourceRef[] = [];
  const lines = ordered.map((g) => {
    if (g.sourceRef) sourceRefs.push(g.sourceRef);
    let when = "no fixed deadline";
    if (g.countdownTo) {
      const ms = Date.parse(g.countdownTo) - now;
      if (Number.isNaN(ms)) {
        when = "deadline date unavailable";
      } else if (ms <= 0) {
        when = "OVERDUE";
      } else {
        const hrs = Math.floor(ms / 3_600_000);
        when = hrs < 48 ? `${hrs}h remaining` : `${Math.floor(hrs / 24)}d remaining`;
      }
    }
    return `• ${g.title} — ${when}\n    ${g.why}\n    Next: ${g.suggestedAction}`;
  });

  const reply =
    `${ordered.length} active time-sensitive deadline${ordered.length === 1 ? "" : "s"} on ${k.caseNumber ?? "this case"}:\n\n` +
    lines.join("\n\n") +
    `\n\nSurfaced from the case's warrant and evidence records — verify against the official notification calendar.`;

  return { reply, sourceRefs: dedupeRefs(sourceRefs) };
}

/**
 * "Draft a warrant for [phone] based on what we have."
 * Returns a SHORT structured stub (parties/accounts/items) from extracted data
 * and instructs the detective to open the Draft dialog. NEVER authors a
 * probable-cause narrative.
 */
function answerDraftWarrant(q: string, k: CaseKnowledge): CaseAnswer | null {
  if (!/\bdraft\b/.test(q) || !/\bwarrant\b/.test(q)) return null;

  // Pull a phone number out of the query, if present.
  const phoneMatch = q.match(/(\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/);
  const target = phoneMatch ? phoneMatch[1] : null;

  // Resolve the phone against the case's phone entities (digit-normalized).
  const phones = k.entities.filter((e) => e.kind === "phone");
  let phoneEntity: IntelligenceEntity | undefined;
  if (target) {
    const want = digits(target);
    phoneEntity = phones.find((e) => digits(e.canonicalIdentifier).endsWith(want.slice(-10)));
  }

  const subjectPhone = phoneEntity?.displayName ?? target ?? "the identified phone number";

  // Parties = the case's named suspects / persons of interest.
  const parties = k.persons
    .filter((p) => p.role === "suspect" || p.role === "personOfInterest" || p.role === "associate")
    .map((p) => `${p.fullName} (${roleLabel(p.role)})`);

  // Accounts/property = phone + any account entities tied to the case.
  const accounts = [
    `${subjectPhone}${phoneEntity ? "" : " (not yet resolved to a case entity — confirm)"}`,
    ...k.entities.filter((e) => e.kind === "account").map((e) => e.displayName),
  ];

  // Items to seize = standard CDR/telecom records package.
  const items = [
    "Call detail records (CDRs) for the offense window",
    "Subscriber and billing information",
    "Cell-site location information (CSLI)",
    "Stored text/SMS and connection logs",
  ];

  // Suggested warrant type for a phone target.
  const suggestedType: WarrantType = "cdr";

  const sourceRefs: SourceRef[] = [];
  if (phoneEntity) {
    const cite = phoneEntity.attributes[0]?.sourceCitation;
    if (cite) sourceRefs.push({ evidenceId: phoneEntity.id, label: cite });
  }

  const reply =
    `Here is a STRUCTURED starting point for a ${WARRANT_TYPE_LABELS[suggestedType]} warrant on ${subjectPhone}, assembled from this case's extracted facts:\n\n` +
    `Parties:\n${parties.length ? parties.map((p) => `  • ${p}`).join("\n") : "  • (none extracted)"}\n\n` +
    `Property / accounts:\n${accounts.map((a) => `  • ${a}`).join("\n")}\n\n` +
    `Items to seize:\n${items.map((i) => `  • ${i}`).join("\n")}\n\n` +
    `Open the Draft dialog on the Warrants tab to review and edit this structured template. ` +
    `You write the probable-cause narrative — IRIS does not author it and never submits anywhere.`;

  return {
    reply,
    sourceRefs: dedupeRefs(sourceRefs),
    draftHandoff: { type: suggestedType, phone: phoneEntity?.canonicalIdentifier ?? target ?? "" },
  };
}

// Ordered so the most specific intents win first.
const MATCHERS: ((q: string, k: CaseKnowledge) => CaseAnswer | null)[] = [
  answerDraftWarrant,
  answerCalecpaDeadlines,
  answerVehicles,
  answerOfficers,
  answerPeopleInReports,
];

/**
 * Try to answer a query from the case knowledge graph alone. Returns `null`
 * when no case-bounded rule matches so the caller can fall back to Bedrock.
 */
export function answerCaseQuery(q: string, k: CaseKnowledge): CaseAnswer | null {
  const norm = q.trim().toLowerCase();
  if (!norm) return null;
  for (const matcher of MATCHERS) {
    const answer = matcher(norm, k);
    if (answer) return answer;
  }
  return null;
}

/** The five spec example questions — seeded into the IRIS empty state. */
export const CASE_SEED_QUESTIONS: string[] = [
  "Who are all the people named in the initial reports?",
  "What vehicles have been identified?",
  "Which officers submitted reports?",
  "What CalECPA deadlines are active?",
  "Draft a warrant for +1 (702) 555-0613 based on what we have.",
];
